/**
 * AddTemplateToListBanner Component
 *
 * Shows a banner when the note was generated with a template that isn't in
 * the user's templates list. Offers to add it to their list for future use.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTemplates } from '@features/templates/hooks/useTemplates';
import { extractTemplateInfo } from '@features/templates/utils';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Slide, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

interface AddTemplateToListBannerProps {
  /** Note data containing template info */
  noteData: unknown;
}

/**
 * Banner that prompts users to add the current note's template to their templates list.
 * Only shows for PUBLIC templates that aren't already in the user's list.
 */
export const AddTemplateToListBanner: React.FC<AddTemplateToListBannerProps> = ({
  noteData,
}) => {
  const theme = useTheme();
  const { template } = useEventTracking();
  const [isAdding, setIsAdding] = useState(false);
  const [wasAdded, setWasAdded] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);
  const bannerShownTimeRef = useRef<number | null>(null);

  // Get user's templates list and addTemplate function
  const { templates, isLoading: isTemplatesLoading, addTemplate } = useTemplates({
    skipLibrary: true,
  });

  // Extract template info from the note
  const templateInfo = useMemo(() => extractTemplateInfo(noteData), [noteData]);

  // Check if the template is already in the user's list
  const isTemplateInUserList = useMemo(() => {
    if (!templateInfo?.id || isTemplatesLoading) {
      return true; // Default to true to hide banner while loading
    }
    return templates.some(t => t.template.id === templateInfo.id);
  }, [templateInfo?.id, templates, isTemplatesLoading]);

  // Only show for PUBLIC templates not in user's list
  const shouldShowBanner = useMemo(() => {
    if (!templateInfo) return false;
    if (templateInfo.ownerScope !== 'public') return false;
    if (isTemplateInUserList) return false;
    if (wasAdded) return false;
    if (wasDismissed) return false;
    return true;
  }, [templateInfo, isTemplateInUserList, wasAdded, wasDismissed]);

  // Track when banner is shown
  useEffect(() => {
    if (shouldShowBanner && templateInfo) {
      bannerShownTimeRef.current = Date.now();
      
      // Extract note_id from noteData if available
      const noteId = (noteData as any)?.id || (noteData as any)?.note_id;
      
      // Map ownerScope to expected values
      const templateScope = templateInfo.ownerScope === 'org' 
        ? 'clinic' 
        : templateInfo.ownerScope === 'user' 
          ? 'personal' 
          : templateInfo.ownerScope as 'public' | 'clinic' | 'personal';
      
      template.addToListBannerShown({
        note_id: noteId,
        template_id: templateInfo.id,
        template_name: templateInfo.name,
        template_scope: templateScope,
      });
    }
  }, [shouldShowBanner, templateInfo, noteData, template]);

  const handleAddTemplate = async () => {
    if (!templateInfo?.id) return;

    // Extract note_id from noteData if available
    const noteId = (noteData as any)?.id || (noteData as any)?.note_id;
    
    // Track add button click
    template.addToListClicked({
      note_id: noteId,
      template_id: templateInfo.id,
      template_name: templateInfo.name,
      source: 'banner',
    });

    setIsAdding(true);
    const success = await addTemplate(templateInfo.id);
    setIsAdding(false);

    if (success) {
      setWasAdded(true);
      bannerShownTimeRef.current = null;
    }
  };

  const handleDismiss = () => {
    const timeVisibleMs = bannerShownTimeRef.current 
      ? Date.now() - bannerShownTimeRef.current 
      : undefined;
    
    // Extract note_id from noteData if available
    const noteId = (noteData as any)?.id || (noteData as any)?.note_id;
    
    // Track banner dismissal
    template.addToListBannerDismissed({
      note_id: noteId,
      template_id: templateInfo?.id,
      template_name: templateInfo?.name,
      time_visible_ms: timeVisibleMs,
    });
    
    setWasDismissed(true);
    bannerShownTimeRef.current = null;
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Slide direction="down" in={shouldShowBanner} mountOnEnter unmountOnExit>
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: '8px',
            bgcolor: '#ffffff',
            borderLeft: `12px solid ${theme.palette.success.main}`,
            boxShadow: '0px 3px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0px 4px 10px rgba(0,0,0,0.18)',
            },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#181E3B',
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              Like this template?
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              Add "{templateInfo?.name}" to your templates list for easy access.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={isAdding}
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            onClick={handleAddTemplate}
          >
            {isAdding ? 'Adding...' : 'Add to My Templates'}
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: 'text.secondary',
              flexShrink: 0,
              '&:hover': {
                color: 'text.primary',
              },
            }}
            aria-label="Dismiss banner"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
};

export default AddTemplateToListBanner;
